import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { CustomFieldDefinition, CustomFieldId, CustomFieldLookupItem, CustomFieldValue } from '@bigint-project/core';

const SUPPORTED_KINDS = ['Text', 'Number', 'Cost', 'Date', 'Flag'] as const;
type ManualTaskCustomFieldKind = (typeof SUPPORTED_KINDS)[number];

export interface TaskCustomFieldCreateInput {
  readonly name: string;
  readonly kind: ManualTaskCustomFieldKind;
  readonly lookup?: readonly CustomFieldLookupItem[];
  readonly defaultValue?: CustomFieldValue;
}

export interface TaskCustomFieldsDialogProps {
  readonly isOpen: boolean;
  readonly definitions: readonly CustomFieldDefinition[];
  readonly onCancel: () => void;
  readonly onCreate: (input: TaskCustomFieldCreateInput) => void;
  readonly onRename: (id: CustomFieldId, name: string) => void;
  readonly onDelete: (id: CustomFieldId) => void;
}

interface Draft {
  name: string;
  kind: ManualTaskCustomFieldKind;
  lookup: string;
  defaultValue: string;
}

const EMPTY_DRAFT: Draft = { name: '', kind: 'Text', lookup: '', defaultValue: '' };

function parseValue(raw: string, kind: ManualTaskCustomFieldKind): CustomFieldValue | null | undefined {
  const value = raw.trim();
  if (value === '') return null;
  if (kind === 'Text') return raw;
  if (kind === 'Number' || kind === 'Cost') {
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  }
  if (kind === 'Flag') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
    return undefined;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value ? undefined : value;
}

function parseLookup(raw: string, kind: ManualTaskCustomFieldKind): readonly CustomFieldLookupItem[] | undefined {
  if (raw.trim() === '') return [];
  const items: CustomFieldLookupItem[] = [];
  for (const line of raw.split('\n')) {
    const [valueText, ...labelParts] = line.split('|');
    const value = parseValue(valueText ?? '', kind);
    const label = labelParts.join('|').trim();
    if (value === undefined || value === null || label === '') return undefined;
    items.push({ value, label });
  }
  return items;
}

function isSupportedDefinition(definition: CustomFieldDefinition): boolean {
  return definition.scope === 'Task'
    && definition.formula === null
    && definition.rollup === 'None'
    && (SUPPORTED_KINDS as readonly string[]).includes(definition.kind);
}

/** 純 UI：只管理 Task 的手動自訂欄位；最終資料完整性由 core command 驗證。 */
export function TaskCustomFieldsDialog(props: TaskCustomFieldsDialogProps): React.JSX.Element | null {
  const { isOpen, definitions, onCancel, onCreate, onRename, onDelete } = props;
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<CustomFieldId | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [deletePending, setDeletePending] = useState<CustomFieldDefinition | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const titleId = useId();
  const descriptionId = useId();

  const supportedDefinitions = useMemo(() => definitions.filter(isSupportedDefinition), [definitions]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setDraft(EMPTY_DRAFT);
      setError(null);
      setRenamingId(null);
      setDeletePending(null);
      returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      nameInputRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const close = (): void => onCancel();

  const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const submitCreate = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const name = draft.name.trim();
    if (name === '') {
      setError('請輸入欄位名稱。');
      return;
    }
    if (definitions.some((definition) => definition.name.trim().toLocaleLowerCase() === name.toLocaleLowerCase())) {
      setError('欄位名稱已存在。');
      return;
    }
    const lookup = parseLookup(draft.lookup, draft.kind);
    if (lookup === undefined) {
      setError('Lookup 每行需為「value|label」，且 value 必須符合欄位型別。');
      return;
    }
    if (lookup.some((item, index) => lookup.slice(0, index).some((prior) => Object.is(prior.value, item.value)))) {
      setError('Lookup value 不可重複。');
      return;
    }
    const defaultValue = parseValue(draft.defaultValue, draft.kind);
    if (defaultValue === undefined) {
      setError('預設值無法依目前欄位型別解析。');
      return;
    }
    if (defaultValue !== null && lookup.length > 0 && !lookup.some((item) => Object.is(item.value, defaultValue))) {
      setError('預設值必須在 Lookup 清單中。');
      return;
    }
    onCreate({
      name,
      kind: draft.kind,
      ...(lookup.length > 0 ? { lookup } : {}),
      ...(defaultValue !== null ? { defaultValue } : {}),
    });
    setDraft(EMPTY_DRAFT);
    setError(null);
  };

  const submitRename = (definition: CustomFieldDefinition): void => {
    const name = renameDraft.trim();
    if (name === '') {
      setError('欄位名稱不可空白。');
      return;
    }
    if (definitions.some((item) => item.id !== definition.id && item.name.trim().toLocaleLowerCase() === name.toLocaleLowerCase())) {
      setError('欄位名稱已存在。');
      return;
    }
    onRename(definition.id, name);
    setRenamingId(null);
    setError(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={trapFocus}
        className="max-h-full w-full max-w-2xl overflow-auto rounded-lg bg-white p-5 shadow-xl dark:bg-slate-900"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold">Task 自訂欄位</h2>
            <p id={descriptionId} className="mt-1 text-sm text-slate-600 dark:text-slate-300">只管理手動輸入欄位；公式與摘要彙總尚未啟用。</p>
          </div>
          <button type="button" onClick={close} aria-label="關閉自訂欄位" className="rounded px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800">×</button>
        </div>

        <form onSubmit={submitCreate} className="mt-5 grid gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
          <h3 className="font-medium">新增欄位</h3>
          <label className="grid gap-1 text-sm">欄位名稱<input ref={nameInputRef} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" /></label>
          <label className="grid gap-1 text-sm">型別<select value={draft.kind} onChange={(event) => setDraft({ ...draft, kind: event.target.value as ManualTaskCustomFieldKind })} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600">{SUPPORTED_KINDS.map((kind) => <option key={kind} value={kind}>{kind}</option>)}</select></label>
          <label className="grid gap-1 text-sm">Lookup（選填，每行 value|label）<textarea value={draft.lookup} onChange={(event) => setDraft({ ...draft, lookup: event.target.value })} rows={3} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" /></label>
          <label className="grid gap-1 text-sm">預設值（選填）<input value={draft.defaultValue} onChange={(event) => setDraft({ ...draft, defaultValue: event.target.value })} className="rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600" /></label>
          {error ? <p role="alert" className="text-sm text-red-600">{error}</p> : null}
          <div><button type="submit" className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500">建立欄位</button></div>
        </form>

        <section aria-labelledby="task-custom-fields-existing" className="mt-5 border-t border-slate-200 pt-4 dark:border-slate-700">
          <h3 id="task-custom-fields-existing" className="font-medium">既有欄位</h3>
          {supportedDefinitions.length === 0 ? <p className="mt-2 text-sm text-slate-500">尚無手動 Task 自訂欄位。</p> : (
            <ul className="mt-2 space-y-2">
              {supportedDefinitions.map((definition) => (
                <li key={definition.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 p-2 dark:border-slate-700">
                  {renamingId === definition.id ? <input aria-label="新欄位名稱" value={renameDraft} onChange={(event) => setRenameDraft(event.target.value)} className="rounded border border-slate-300 bg-transparent px-2 py-1 dark:border-slate-600" /> : <span>{definition.name} <span className="text-xs text-slate-500">{definition.kind}</span></span>}
                  <span className="flex gap-2"><button type="button" onClick={() => renamingId === definition.id ? submitRename(definition) : (setRenamingId(definition.id), setRenameDraft(definition.name))} className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600">{renamingId === definition.id ? '儲存名稱' : '改名'}</button><button type="button" onClick={() => setDeletePending(definition)} className="rounded border border-red-300 px-2 py-1 text-sm text-red-700 dark:border-red-800 dark:text-red-300">刪除</button></span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {deletePending ? (
          <div role="alertdialog" aria-modal="true" aria-label="確認刪除自訂欄位" className="mt-4 rounded border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30">
            <p className="text-sm">確定刪除「{deletePending.name}」？這會清除所有任務的此欄位值。</p>
            <div className="mt-3 flex justify-end gap-2"><button type="button" onClick={() => setDeletePending(null)} className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600">取消</button><button type="button" onClick={() => { onDelete(deletePending.id); setDeletePending(null); }} className="rounded bg-red-600 px-2 py-1 text-sm text-white">確認刪除</button></div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default TaskCustomFieldsDialog;
